import * as React from "react";
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  IconButton,
  Menu,
  Container,
  MenuItem,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

const navLinks = [
  { text: "Home", section: "home" },
  { text: "How It Works", section: "howItWorks" },
  { text: "Features", section: "features" },
  { text: "Health Wallet", section: "wallet" },
  { text: "For Doctors", section: "doctors" },
  { text: "Terms & Conditions", href: "/terms-and-conditions" },
  { text: "Privacy Policy", href: "/privacy-policy" },
];

const ResponsiveAppBar = ({ scrollToSection }) => {
  const [anchorElNav, setAnchorElNav] = React.useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        bgcolor: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)",
        borderBottom: "1px solid #EEF2F6",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: "72px", justifyContent: "space-between" }}>
          {/* Brand Logo */}
          <Box
            component="a"
            href="/"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              gap: 1.5,
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #135BEC 0%, #0B48C5 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(19, 91, 236, 0.3)",
              }}
            >
              <LocalHospitalIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 800,
                  color: "#0F172A",
                  lineHeight: 1.2,
                  letterSpacing: "-0.5px",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                Telemedicine
              </Typography>
              <Typography
                sx={{
                  fontSize: "10.5px",
                  fontWeight: 600,
                  color: "#135BEC",
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                }}
              >
                Your Health, Anywhere
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1,
            }}
          >
            {navLinks.slice(0, 5).map((link, index) =>
              link.href ? (
                <Button
                  key={index}
                  href={link.href}
                  sx={{
                    my: 1,
                    px: 1.8,
                    color: "#475569",
                    fontWeight: 600,
                    fontSize: "14.5px",
                    textTransform: "none",
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#135BEC",
                      bgcolor: "rgba(19, 91, 236, 0.05)",
                    },
                  }}
                >
                  {link.text}
                </Button>
              ) : (
                <Button
                  key={index}
                  onClick={(e) => {
                    if (link.section && scrollToSection) {
                      e.preventDefault();
                      scrollToSection(link.section);
                    }
                  }}
                  sx={{
                    my: 1,
                    px: 1.8,
                    color: "#475569",
                    fontWeight: 600,
                    fontSize: "14.5px",
                    textTransform: "none",
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#135BEC",
                      bgcolor: "rgba(19, 91, 236, 0.05)",
                    },
                  }}
                >
                  {link.text}
                </Button>
              )
            )}
          </Box>

          {/* Action CTAs */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Button
              variant="text"
              startIcon={<VerifiedUserIcon sx={{ fontSize: 18 }} />}
              onClick={() => {
                if (scrollToSection) scrollToSection("doctors");
              }}
              sx={{
                color: "#135BEC",
                fontWeight: 600,
                fontSize: "14px",
                textTransform: "none",
                px: 2,
                py: 1,
                borderRadius: "10px",
                "&:hover": {
                  bgcolor: "rgba(19, 91, 236, 0.08)",
                },
              }}
            >
              Doctor Portal
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (scrollToSection) scrollToSection("home");
              }}
              sx={{
                bgcolor: "#135BEC",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "14px",
                textTransform: "none",
                px: 2.8,
                py: 1.1,
                borderRadius: "10px",
                boxShadow: "0 4px 14px rgba(19, 91, 236, 0.35)",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#0D4BCA",
                  boxShadow: "0 6px 18px rgba(19, 91, 236, 0.45)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Consult Now
            </Button>
          </Box>

          {/* Mobile Menu Icon */}
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              onClick={handleOpenNavMenu}
              sx={{ color: "#0F172A" }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: "block", md: "none" },
                mt: 1.5,
                "& .MuiPaper-root": {
                  borderRadius: "16px",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                  minWidth: "220px",
                  p: 1,
                },
              }}
            >
              {navLinks.map((link, index) => (
                <MenuItem
                  key={index}
                  component="a"
                  href={link.href || "/"}
                  onClick={(e) => {
                    if (link.section && scrollToSection) {
                      e.preventDefault();
                      scrollToSection(link.section);
                    }
                    handleCloseNavMenu();
                  }}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 500,
                    color: "#1E293B",
                    my: 0.5,
                    "&:hover": {
                      color: "#135BEC",
                      bgcolor: "rgba(19, 91, 236, 0.06)",
                    },
                  }}
                >
                  {link.text}
                </MenuItem>
              ))}
              <Box sx={{ p: 1, pt: 1.5, borderTop: "1px solid #F1F5F9" }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    handleCloseNavMenu();
                    if (scrollToSection) scrollToSection("home");
                  }}
                  sx={{
                    bgcolor: "#135BEC",
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#0D4BCA" },
                  }}
                >
                  Consult Now
                </Button>
              </Box>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default ResponsiveAppBar;
